import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Download, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Checkbox } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { formatPhone } from "../../../../lib/phone";
import {
  listUnknownRecordings, deleteUnknownRecordings, unknownRecordingAudioUrl,
  type UnknownListing, type UnknownRecording, type UnknownDeleteResult,
} from "../../../../lib/calls";
import { Btn, listCard } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";

const PAGE_SIZE = 25;
/** The API caps one delete call; a bigger selection goes out in several. */
const DELETE_CHUNK = 200;

/**
 * Select, when, how long, between whom, and the audio itself.
 *
 * The audio track is the wide one: it holds a player and a download link, which
 * is why the rows are the softer-cornered variant - a 40px-tall player does not
 * sit inside a 999px pill.
 */
const COLS = "36px 1.1fr .7fr minmax(0,1.6fr) minmax(280px,1.5fr)";

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" });
}

function formatDuration(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
}

/**
 * Settings -> Integrations: the unknown Twilio recordings, one page at a time.
 *
 * These are the ones nothing deletes on its own - Twilio holds them, no call log
 * row claims them, and we have no copy of our own. So the panel exists to make
 * them identifiable before anyone acts: when the call happened, how long it was,
 * which numbers it was between, and the audio itself, played straight from
 * Twilio through our own authed proxy.
 *
 * Selection survives paging (a Set of sids), which is why deleting takes the
 * sids and not "everything you can currently see".
 *
 * `onDeleted` lets the card above re-run its own scan, so the headline counts do
 * not keep claiming recordings that are gone.
 *
 * Rows are pills rather than table cells. This was the last `<table>` left in
 * the CRM outside a genuine comparison matrix, and it had that era's habits: a
 * 500-weight column head where the design asks for 800, and shared-kit buttons
 * in a panel whose neighbours had all moved on.
 */
export function UnknownRecordingsPanel({ onDeleted }: { onDeleted?: () => void }) {
  const t = useT();
  const [listing, setListing] = useState<UnknownListing | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<UnknownDeleteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (at: number) => {
    setLoading(true);
    setError(null);
    try {
      // detail: the numbers are what makes a row identifiable, and only the
      // visible page is enriched - it is one Twilio lookup per row.
      setListing(await listUnknownRecordings({ offset: at, pageSize: PAGE_SIZE, detail: true }));
    } catch {
      setError(t("settings.recordingStorage.unknown.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load(offset);
  }, [load, offset]);

  function toggle(sid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  }

  const items = listing?.items ?? [];
  const pageSids = items.map((r) => r.sid);
  const pageAllSelected = pageSids.length > 0 && pageSids.every((sid) => selected.has(sid));

  function togglePage() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const sid of pageSids) {
        if (pageAllSelected) next.delete(sid);
        else next.add(sid);
      }
      return next;
    });
  }

  async function handleDelete() {
    setError(null);
    const sids = [...selected];
    const total: UnknownDeleteResult = { deleted: 0, failed: 0, skipped: 0, savedMonthlyUsd: 0 };
    try {
      for (let i = 0; i < sids.length; i += DELETE_CHUNK) {
        const part = await deleteUnknownRecordings(sids.slice(i, i + DELETE_CHUNK));
        total.deleted += part.deleted;
        total.failed += part.failed;
        total.skipped += part.skipped;
        total.savedMonthlyUsd += part.savedMonthlyUsd;
      }
      setResult(total);
      setSelected(new Set());
      setOffset(0);
      await load(0);
      onDeleted?.();
    } catch {
      setError(t("settings.recordingStorage.unknown.deleteFailed"));
    } finally {
      setConfirming(false);
    }
  }

  const total = listing?.total ?? 0;
  const shown = items.length;

  return (
    <div className="crm-card" style={listCard(COLS)}>
      <div className="flex flex-wrap items-center gap-3 px-[26px] pt-[22px]">
        <h4 className="crm-card-t">{t("settings.recordingStorage.unknown.title")}</h4>
        <Btn small className="crm-push" onClick={() => void load(offset)} disabled={loading}>
          <RefreshCw className={loading ? "size-3.5 animate-spin" : "size-3.5"} />
          {t("settings.recordingStorage.unknown.reload")}
        </Btn>
      </div>

      <div className="space-y-2.5 px-[26px] pt-3.5">
        <p className="crm-alert text-[13px]">
          <AlertTriangle className="size-4 shrink-0" />
          {t("settings.recordingStorage.unknown.warning")}
        </p>

        {result && (
          <p className="rounded-2xl bg-[var(--crm-sunk)] px-4 py-3 text-sm text-muted-foreground">
            {t("settings.recordingStorage.unknown.deleted", {
              deleted: result.deleted,
              usd: result.savedMonthlyUsd.toFixed(2),
              failed: result.failed,
              skipped: result.skipped,
            })}
          </p>
        )}
        {error && <p className="crm-alert text-[13px]">{error}</p>}
      </div>

      {total === 0 && !loading ? (
        <p className="px-[26px] py-6 text-sm text-muted-foreground">
          {t("settings.recordingStorage.unknown.empty")}
        </p>
      ) : (
        <>
          <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
            <span className="flex items-center">
              <Checkbox
                checked={pageAllSelected}
                onCheckedChange={togglePage}
                aria-label={t("settings.recordingStorage.unknown.selectPage")}
              />
            </span>
            <span>{t("settings.recordingStorage.unknown.col.when")}</span>
            <span>{t("settings.recordingStorage.unknown.col.duration")}</span>
            <span>{t("settings.recordingStorage.unknown.col.numbers")}</span>
            <span>{t("settings.recordingStorage.unknown.col.audio")}</span>
          </div>

          <div className="crm-rows pt-0">
            {loading && items.length === 0 ? (
              <div className="flex justify-center py-10 text-muted-foreground">
                <Loader2 className="size-6 animate-spin" />
              </div>
            ) : (
              items.map((row) => (
                <Row key={row.sid} row={row} checked={selected.has(row.sid)} onToggle={() => toggle(row.sid)} />
              ))
            )}
          </div>
        </>
      )}

      <div className="crm-tfoot flex-wrap">
        <span>
          {t("settings.recordingStorage.unknown.pageInfo", {
            from: total === 0 ? 0 : offset + 1,
            to: offset + shown,
            total,
          })}
        </span>
        <Btn
          small
          round
          disabled={offset === 0 || loading}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          aria-label={t("settings.recordingStorage.unknown.prevPage")}
        >
          <ChevronLeft className="size-4" />
        </Btn>
        <Btn
          small
          round
          disabled={offset + shown >= total || loading}
          onClick={() => setOffset(offset + PAGE_SIZE)}
          aria-label={t("settings.recordingStorage.unknown.nextPage")}
        >
          <ChevronRight className="size-4" />
        </Btn>

        <Btn small disabled={loading || total === 0} onClick={() => setSelected(new Set(listing?.allSids ?? []))}>
          {t("settings.recordingStorage.unknown.selectAll", { count: total })}
        </Btn>
        <Btn
          small
          tone="danger"
          className="crm-push"
          disabled={selected.size === 0}
          onClick={() => setConfirming(true)}
        >
          <Trash2 className="size-3.5" />
          {t("settings.recordingStorage.unknown.deleteSelected", { count: selected.size })}
        </Btn>
      </div>

      {confirming ? (
        <ConfirmModal
          title={t("settings.recordingStorage.unknown.confirmTitle")}
          body={t("settings.recordingStorage.unknown.confirmBody", { count: selected.size })}
          confirmLabel={t("settings.recordingStorage.unknown.confirmYes")}
          cancelLabel={t("settings.recordingStorage.unknown.confirmNo")}
          onConfirm={handleDelete}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

function Row({ row, checked, onToggle }: { row: UnknownRecording; checked: boolean; onToggle: () => void }) {
  const t = useT();
  const from = row.call?.from ? formatPhone(row.call.from) : null;
  const to = row.call?.to ? formatPhone(row.call.to) : null;

  return (
    // The soft variant: an audio player is 32px tall and a 999px pill around it
    // reads as a capsule with a rectangle inside.
    <div className="crm-list-row crm-list-row-soft" style={{ gridTemplateColumns: COLS }}>
      <span className="flex items-center">
        <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={row.sid} />
      </span>

      <span className="crm-tnum text-[12.5px]">{formatWhen(row.createdAt)}</span>
      <span className="crm-tnum text-[12.5px]">{formatDuration(row.durationSec)}</span>

      <span className="min-w-0">
        {from || to ? (
          <span className="crm-prow-n crm-trunc block">
            {from ?? "?"} {"->"} {to ?? "?"}
          </span>
        ) : (
          <span className="crm-mut text-[12.5px]">{t("settings.recordingStorage.unknown.noCall")}</span>
        )}
        {/* The sid is how a recording is named in Twilio's own console, which is
            where anyone checking our arithmetic will go next. */}
        <span className="crm-prow-s crm-trunc block font-mono">{row.sid}</span>
      </span>

      <span className="flex min-w-0 items-center gap-2">
        {/* Same-origin and cookie-authed, so the plain element works and the
            Twilio credentials never reach the browser. */}
        <audio controls preload="none" className="h-8 min-w-0 flex-1" src={unknownRecordingAudioUrl(row.sid)} />
        <a
          href={unknownRecordingAudioUrl(row.sid)}
          download={`${row.sid}.mp3`}
          className="crm-cb crm-cb-sm shrink-0"
          title={t("settings.recordingStorage.unknown.download")}
          aria-label={t("settings.recordingStorage.unknown.download")}
        >
          <Download className="size-4" />
        </a>
      </span>
    </div>
  );
}
